<template>
  <div class="end-turn-container">
    <!-- Small End Turn Button -->
    <button
        class="gm-btn gm-btn--primary end-turn-button"
        :disabled="isSwitching"
        :title="isSwitching ? 'Switching turn…' : 'End Turn'"
        @click="confirmEndTurn"
    >
      <span v-if="isSwitching" class="dx-loader dx-loader--xs" aria-hidden="true"></span>
      {{ isSwitching ? 'Switching…' : 'End Turn' }}
    </button>

    <!-- Confirmation Modal -->
    <div v-if="isConfirming" class="modal-overlay" @click.self="cancelConfirmation">
      <div class="gm-panel gm-panel--raised modal-content">
        <h3 class="gm-title">End Turn for All Players?</h3>
        <div class="modal-actions">
          <button class="gm-btn gm-btn--ghost" @click="cancelConfirmation">No</button>
          <button class="gm-btn gm-btn--primary" @click="endTurn">Yes</button>
        </div>
      </div>
    </div>

    <!-- Switching Overlay: the cycle runs every character's actions server-side,
         which takes seconds, so the wait needs a visible owner. -->
    <div v-if="isSwitching" class="modal-overlay switching-overlay" role="status" aria-live="polite">
      <div class="gm-panel gm-panel--raised modal-content switching-content">
        <span class="dx-loader dx-loader--lg" aria-hidden="true"></span>
        <h3 class="gm-title">Switching turn…</h3>
        <p class="switching-note">Resolving effects, actions and NPC behaviour for every character.</p>
      </div>
    </div>

    <!-- Success/Failure Popup -->
    <div v-if="popupMessage" :class="['gm-chip', 'popup', popupType]">
      {{ popupMessage }}
    </div>
  </div>
</template>

<script>
import { ActionGameApi } from "@/api/backendService.js";

export default {
  name: "EndTurnComponent",
  emits: ["switch-start", "switch-end"],
  data() {
    return {
      isConfirming: false, // Tracks if confirmation modal is open
      isSwitching: false, // Tracks the in-flight cycle switch
      popupMessage: null, // Message to display in the popup
      popupType: "", // Type of popup: success or error
    };
  },
  methods: {
    confirmEndTurn() {
      if (this.isSwitching) return;
      this.isConfirming = true; // Show the confirmation modal
    },
    cancelConfirmation() {
      this.isConfirming = false; // Hide the confirmation modal
    },
    async endTurn() {
      if (this.isSwitching) return;
      this.isConfirming = false; // Hide the confirmation modal
      this.isSwitching = true;
      this.$emit("switch-start");

      try {
        const {data} = await ActionGameApi.actionNextCycleCreate();
        const phase = data?.phase ? ` — ${data.phase.toLowerCase()}` : "";
        this.showPopup(`Cycle ${data?.number ?? ""}${phase}`.trim(), "success");
      } catch (error) {
        this.showPopup("Failed to end turn. Please try again.", "error");
      } finally {
        this.isSwitching = false;
        this.$emit("switch-end");
      }
    },
    showPopup(message, type) {
      this.popupMessage = message;
      this.popupType = type;
      setTimeout(() => {
        this.popupMessage = null; // Clear popup after 3 seconds
      }, 3000);
    },
  },
};
</script>

<style scoped>
.end-turn-container {
  display: flex;
}

.end-turn-button {
  min-width: 6.5rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  text-align: center;
  width: 90%;
  max-width: 400px;
}

.switching-overlay {
  z-index: 1500;
  cursor: progress;
}

.switching-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--gm-gap-sm);
}

.switching-note {
  margin: 0;
  font-size: 0.7rem;
  color: var(--gm-text-dim);
}

.modal-actions {
  display: flex;
  justify-content: center;
  gap: var(--gm-gap-sm);
  margin-top: var(--gm-gap);
}

.popup {
  position: fixed;
  bottom: calc(var(--gm-dock-clearance) + 1rem);
  left: 50%;
  transform: translateX(-50%);
  z-index: 2000;
  animation: fadeInOut 3s ease;
}

.popup.success {
  color: var(--gm-success);
  border-color: var(--gm-success);
}

.popup.error {
  color: var(--gm-danger);
  border-color: var(--gm-danger);
}

@keyframes fadeInOut {
  0% {
    opacity: 0;
    transform: translateX(-50%) translateY(20px);
  }
  10%,
  90% {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
  100% {
    opacity: 0;
    transform: translateX(-50%) translateY(20px);
  }
}
</style>
